import React, { useEffect, useMemo, useState } from "react";
import { Copy, RefreshCw, CheckCircle, XCircle, Phone } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

import { DonutChart } from "../../components/admin/organization/SummaryDoughnut";
import { OrganizationDto, OrganizationsClient } from "../../web-api-client";
import { makeFetchWithAuth } from "../../auth/authFetch";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { useNavigate } from "react-router-dom";
import { toUserMessage } from "../../utils/toUserMessage";
import { fetchOrganizationLinkToken } from "../../hooks/organizations/organizationApi";
import { format } from "date-fns";
import { Skeleton } from "../../components/ui/skeleton";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { CreateLinkButton } from "../../components/admin/organization/CreateLinkButton";
import Alert from "../../components/ui/Alert";
import { useUpdateOrganization } from "../../hooks/organizations/useOrganizationMutations";
import { UpdateOrganizationCommand } from "../../web-api-client";
import { can, Perm } from "../../auth/permissions";
import { appConfig } from "../../appconfig";

type OrganizationLink = {
  id: number;
  organizationId: number;
  tokenPrefix?: string | null;
  isEnabled: boolean;
  rotatedAt?: string | null;
  created?: string | null;
};

type OrganizationCounters = {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  noShow: number;
};

export type OrganizationListItem = {
  id: number;
  name: string;
  code?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  note?: string | null;
  isActive: boolean;
  created: string;
  link?: OrganizationLink | null;
  counters: OrganizationCounters;
};

type OrganizationStats = {
  organizationId: number;
  organizationName: string;
  totalRegistrations: number;
  statusCounts: Record<string, number>;
  breakdown: Array<{ key: string; count: number }>;
  averageLeadTimeDays?: number | null;
  averageProcessingMinutes?: number | null;
};

type OrganizationBooking = {
  id: number;
  patientKey: string;
  roomId: number;
  doctorId?: number | null;
  organizationId?: number | null;
  date: string;
  timeband: number;
  status: number;
  hisAppointmentId: number;
  created: string;
  lastModified: string;
};

type PaginatedResponse<T> = {
  items: T[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
};

export type OrganizationDetailProps = {
  organization: OrganizationDto | null;
  from: Date;
  to: Date;
  tokenOverrides: Record<number, string>; // orgId -> plain token (chỉ có ngay sau tạo/reset)
  onTokenResolved?: (orgId: number, token: string) => void;
  onResetLink: (orgId: number) => Promise<void> | void;
  onToggleLink: (orgId: number, enabled: boolean) => Promise<void> | void;
  onRefresh: () => void;
};

const buildRegistrationLink = (token: string) =>
  `${window.location.origin}?token=${token}`;

const formatDateParam = (date: Date) => format(date, "yyyy-MM-dd");

const timebandLabel = (band?: number) => {
  switch (band) {
    case 1:
      return "Sáng 1";
    case 2:
      return "Sáng 2";
    case 3:
      return "Chiều 1";
    default:
      return "--";
  }
};

const formatBookingStatus = (status?: number) => {
  switch (status) {
    case 1:
      return {
        label: "Đã xác nhận",
        className:
          "bg-green-100 text-green-700 hover:bg-green-200 border-green-200",
      };
    case 2:
      return {
        label: "Đã hủy",
        className: "bg-red-100 text-red-700 hover:bg-red-200 border-red-200",
      };
    case 0:
    default:
      return {
        label: "Chờ xử lý",
        className:
          "bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-200",
      };
  }
};

export default function OrganizationDetail({
  organization,
  from,
  to,
  tokenOverrides,
  onTokenResolved,
  onResetLink,
  onToggleLink,
  onRefresh,
}: OrganizationDetailProps) {
  const canManange = can(Perm.Organization_Manage);

  const { toast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");

  const [statsByDay, setStatsByDay] = useState<OrganizationStats | null>(null);
  const [statsByTimeband, setStatsByTimeband] =
    useState<OrganizationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [appointments, setAppointments] = useState<OrganizationBooking[]>([]);
  const [appointmentsTotal, setAppointmentsTotal] = useState(0);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [appointmentsPageSize, setAppointmentsPageSize] = useState(20);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [note, setNote] = useState("");

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const updateOrganization = useUpdateOrganization();
  const isBusy = updateOrganization.isPending;

  const authFetch = useMemo(
    () =>
      makeFetchWithAuth(fetchWithTimeout(), {
        onUnauthorized: () =>
          navigate("/login", {
            replace: true,
            state: { from: window.location.pathname },
          }),
      }),
    [navigate]
  );

  const organizationsClient = useMemo(
    () => new OrganizationsClient(appConfig.apiBaseUrl, { fetch: authFetch }),
    [authFetch]
  );

  useEffect(() => {
    if (!organization) return;

    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const [dayStats, timebandStats] = await Promise.all([
          organizationsClient.getApiOrganizationsStats(
            organization.id!,
            null,
            null,
            "day"
          ),
          organizationsClient.getApiOrganizationsStats(
            organization.id!,
            null,
            null,
            "timeband"
          ),
        ]);

        setStatsByDay(dayStats as unknown as OrganizationStats);
        setStatsByTimeband(timebandStats as unknown as OrganizationStats);
      } catch (error: any) {
        const message = await toUserMessage(error);
        toast({
          variant: "destructive",
          title: "Không thể tải thống kê",
          description: message,
        });
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, [organization?.id, from, to, organizationsClient, toast]);

  useEffect(() => {
    if (!organization) return;

    const loadAppointments = async () => {
      setAppointmentsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("pageNumber", String(appointmentsPage));
        params.set("pageSize", String(appointmentsPageSize));
        params.set("OrganizationId", String(organization.id));

        const response = await authFetch(
          `/api/organizations/${
            organization.id
          }/appointments?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(await response.text());
        }

        const payload =
          (await response.json()) as PaginatedResponse<OrganizationBooking>;
        setAppointments(payload.items ?? []);
        setAppointmentsTotal(payload.totalCount ?? 0);
      } catch (error: any) {
        const message = await toUserMessage(error);
        toast({
          variant: "destructive",
          title: "Không thể tải danh sách đăng ký",
          description: message,
        });
      } finally {
        setAppointmentsLoading(false);
      }
    };

    loadAppointments();
  }, [
    organization?.id,
    appointmentsPage,
    appointmentsPageSize,
    from,
    to,
    authFetch,
    toast,
  ]);

  useEffect(() => {
    setNote(organization?.note ?? "");
  }, [organization]);

  const filteredAppointments = useMemo(() => {
    if (!searchTerm) return appointments;
    return appointments.filter((apt) =>
      apt.patientKey?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [appointments, searchTerm]);

  const statusCounts = statsByTimeband?.statusCounts ?? {};
  const totalRegistrations = statsByTimeband?.totalRegistrations ?? 0;
  const pendingCount = statusCounts["Held"] ?? 0;
  const confirmedCount = statusCounts["Confirmed"] ?? 0;
  const cancelledCount = statusCounts["Cancelled"] ?? 0;

  const slices = [
    { label: "Xác nhận", value: confirmedCount, color: "#00C875" },
    { label: "Chờ xử lý", value: pendingCount, color: "#FDAB3D" },
    { label: "Bị hủy", value: cancelledCount, color: "#EF4444" },
  ];

  const dailyData = (statsByDay?.breakdown ?? []).map((bucket) => ({
    name: bucket.key,
    "Đăng ký": bucket.count,
  }));

  const hourlyData = (statsByTimeband?.breakdown ?? []).map((bucket) => ({
    name: timebandLabel(Number(bucket.key)),
    "Số lượng": bucket.count,
  }));

  const fullToken = useMemo(() => {
    if (!organization) return null;
    return tokenOverrides[organization.id!] ?? null;
  }, [organization, tokenOverrides]);

  const linkDisplay = fullToken
    ? buildRegistrationLink(fullToken)
    : organization?.link?.tokenPrefix
    ? `${organization.link.tokenPrefix}`
    : "Chưa có link";

  const totalPages = Math.max(
    1,
    Math.ceil(appointmentsTotal / appointmentsPageSize)
  );

  const handleCopyLink = async () => {
    let token = fullToken;
    if (!token && organization?.id) {
      try {
        const response = await fetchOrganizationLinkToken(organization.id);
        if (response.token) {
          token = response.token;
          onTokenResolved?.(organization.id!, response.token);
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Không lấy được token",
          description: toUserMessage(error),
        });
        return;
      }
    }

    if (!token) {
      toast({
        variant: "destructive",
        title: "Chưa có token",
        description: "Vui lòng reset hoặc tạo link để lấy token hợp lệ.",
      });
      return;
    }

    await navigator.clipboard.writeText(buildRegistrationLink(token));
    toast({
      title: "Đã sao chép",
      description: "Link đăng ký đã được sao chép vào clipboard.",
    });
  };

  if (!organization) {
    return (
      <Card className="p-6">
        <p className="text-black-500">Chọn một nhóm để xem chi tiết.</p>
      </Card>
    );
  }

  return canManange ? (
    <>
      {/* Header */}
      <div className="flex flex-col space-y-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex items-start gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-brand tracking-tight mb-2 md:mb-4">
                {organization.name}
              </h1>
              <div className="flex flex-col md:flex-row md:items-center items-start gap-4 mt-2 text-sm md:text-base text-black-500">
                <span className="flex items-center gap-1">
                  Người liên hệ:
                  <span className="font-bold text-base text-brand ml-2">
                    {organization.contactName ?? "--"}
                  </span>
                </span>
                <span className="flex gap-1">
                  Số điện thoại:
                  <Badge variant="outline" className="font-mono text-sm">
                    {organization.contactPhone ?? "--"}
                  </Badge>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          // Recharts cần kích thước container sau khi tab hiện
          requestAnimationFrame(() =>
            window.dispatchEvent(new Event("resize"))
          );
        }}
        className="space-y-6"
      >
        <TabsList className="bg-background border-b rounded-none h-auto p-0 w-full justify-start space-x-6">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:shadow-none rounded-none px-0 py-3"
          >
            Tổng quan
          </TabsTrigger>
          <TabsTrigger
            value="registrations"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:shadow-none rounded-none px-0 py-3"
          >
            Đăng ký
          </TabsTrigger>
          <TabsTrigger
            value="config"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:shadow-none rounded-none px-0 py-3"
          >
            Link đăng ký
          </TabsTrigger>
          <TabsTrigger
            value="reports"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:shadow-none rounded-none px-0 py-3"
          >
            Báo cáo
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent
          value="overview"
          className="space-y-6 animate-in fade-in-50 duration-300"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[0, 1, 2, 3].map((index) => (
              <Card key={`stat-${index}`}>
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium text-black-500">
                    {index === 0 && "Tổng đăng ký"}
                    {index === 1 && "Đã xác nhận"}
                    {index === 2 && "Chờ xử lý"}
                    {index === 3 && "Bị hủy"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {statsLoading ? (
                    <Skeleton className="h-6 w-16" />
                  ) : (
                    <div
                      className={
                        index === 1
                          ? "text-2xl font-semibold text-green-600"
                          : index === 2
                          ? "text-2xl font-semibold text-yellow-600"
                          : index === 3
                          ? "text-2xl font-semibold text-red-500"
                          : "text-2xl font-semibold"
                      }
                    >
                      {index === 0 && totalRegistrations}
                      {index === 1 && confirmedCount}
                      {index === 2 && pendingCount}
                      {index === 3 && cancelledCount}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <DonutChart title="Đăng ký" slices={slices} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-t-4 border-t-primary rounded border-b-green-400 border-l-green-400 border-r-green-400">
              <CardHeader>
                <CardTitle className="text-xl">Diễn biến đăng ký</CardTitle>
                <CardDescription>Biến động đăng ký theo ngày</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <Tooltip
                        formatter={(value: number) => [
                          `${value} lượt`,
                          "Đăng ký",
                        ]}
                        labelFormatter={(label) => `Ngày ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="Đăng ký"
                        stroke="#0ea5e9"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded border-t-4 border-t-primary border-b-green-400 border-l-green-400 border-r-green-400">
              <CardHeader>
                <CardTitle className="text-xl">Phân bố theo giờ</CardTitle>
                <CardDescription>Lượt đăng ký theo khung giờ</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <Tooltip
                        cursor={{ fill: "transparent" }}
                        formatter={(value: number) => [
                          `${value} lượt`,
                          "Số lượng",
                        ]}
                      />
                      <Bar
                        dataKey="Số lượng"
                        fill="#0ea5e9"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Registrations */}
        <TabsContent
          value="registrations"
          className="animate-in fade-in-50 duration-300"
        >
          <Card className="border-none">
            <CardHeader className="p-0 pb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="text-xl">Danh sách đăng ký</CardTitle>
                <div className="flex gap-2">
                  <Input
                    placeholder="Tìm theo SĐT..."
                    className="w-[250px]"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mã phiếu</TableHead>
                    <TableHead>Bệnh nhân</TableHead>
                    <TableHead>Ngày & Giờ</TableHead>
                    <TableHead>Bác sĩ/Phòng</TableHead>
                    <TableHead>Trạng thái</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {appointmentsLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={`loading-${index}`}>
                        <TableCell colSpan={6}>
                          <Skeleton className="h-10 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : filteredAppointments.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-black-500"
                      >
                        Không có dữ liệu
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAppointments.map((apt) => {
                      const statusInfo = formatBookingStatus(apt.status);
                      return (
                        <TableRow key={apt.id}>
                          <TableCell className="font-mono text-xs">
                            HIS-{apt.hisAppointmentId}
                          </TableCell>

                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {apt.patientKey}
                              </span>
                              <span className="text-xs text-black-500">
                                ID: {apt.id}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {format(new Date(apt.date), "dd/MM/yyyy")}
                              </span>
                              <span className="text-xs text-black-500">
                                {timebandLabel(apt.timeband)}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                Bác sĩ {apt.doctorId ?? "--"}
                              </span>
                              <span className="text-xs text-black-500">
                                Phòng {apt.roomId}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <Badge className={statusInfo.className}>
                              {statusInfo.label}
                            </Badge>
                          </TableCell>

                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-green-600 hover:bg-green-50 hover:text-green-700"
                                disabled
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                                disabled
                              >
                                <Phone className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-400 hover:bg-red-50 hover:text-red-500"
                                disabled
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-black-500">
                  <span>Hiển thị</span>
                  <Select
                    value={String(appointmentsPageSize)}
                    onValueChange={(val) => {
                      setAppointmentsPageSize(Number(val));
                      setAppointmentsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                  <span>trong {appointmentsTotal} kết quả</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setAppointmentsPage((p) => Math.max(1, p - 1))
                    }
                    disabled={appointmentsPage === 1 || appointmentsLoading}
                  >
                    Trước
                  </Button>

                  <span className="text-sm text-black-500 px-2">
                    Trang {appointmentsPage} / {totalPages}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setAppointmentsPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={
                      appointmentsPage >= totalPages || appointmentsLoading
                    }
                  >
                    Sau
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Link & Config */}
        <TabsContent
          value="config"
          className="animate-in fade-in-50 duration-300"
        >
          <div className="grid grid-cols-2 gap-6 w-full">
            <Card className="border-t-4 border-t-primary rounded border-b-green-400 border-l-green-400 border-r-green-400">
              <CardHeader>
                <CardTitle>Link đăng ký</CardTitle>
                <CardDescription>
                  Quản lý link đăng ký công khai cho tổ chức này.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Link</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-muted p-3 rounded-md font-mono text-sm border flex items-center text-black-500 overflow-hidden">
                      {linkDisplay}
                    </div>
                    {organization.link ? (
                      <Button
                        onClick={handleCopyLink}
                        variant="outline"
                        className="shrink-0"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    ) : (
                      <CreateLinkButton orgId={organization.id!} />
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <div className="font-medium text-base">Trạng thái</div>
                    <div className="text-sm text-black-500">
                      {organization.link?.isEnabled
                        ? "Hiện đang mở đăng ký"
                        : "Đã khóa đăng ký"}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "text-sm font-medium",
                        organization.link?.isEnabled
                          ? "text-green-600"
                          : "text-black-500"
                      )}
                    >
                      {organization.link?.isEnabled ? "Active" : "Locked"}
                    </span>

                    <Switch
                      checked={organization.link?.isEnabled ?? false}
                      onCheckedChange={(checked) =>
                        onToggleLink(Number(organization.id), checked)
                      }
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <Button
                    variant="destructive"
                    className="w-full sm:w-auto"
                    onClick={() => setResetDialogOpen(true)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Link Token
                  </Button>

                  <Alert
                    className="mt-4 text-sm"
                    variant="warning"
                    msg="Thao tác này sẽ vô hiệu hóa link hiện tại. Link đăng ký
                      cũ sẽ không còn sử dụng được nữa."
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="h-fit border-t-4 border-t-primary rounded border-b-green-400 border-l-green-400 border-r-green-400">
              <CardHeader>
                <CardTitle>Ghi chú nội bộ</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <textarea
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Thêm ghi chú..."
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                  <Button
                    className="mt-2 "
                    disabled={isBusy}
                    onClick={() =>
                      updateOrganization.mutate(
                        {
                          id: organization.id!,
                          payload: new UpdateOrganizationCommand({
                            name: organization.name,
                            code: organization.code,
                            contactName: organization.contactName,
                            contactPhone: organization.contactPhone,
                            note,
                          }),
                        },
                        {
                          onSuccess: () =>
                            toast({
                              variant: "success",
                              title: "Đã cập nhật",
                              description: "Ghi chú đã được lưu.",
                            }),
                        }
                      )
                    }
                  >
                    Lưu ghi chú
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reports */}
        <TabsContent
          value="reports"
          className="animate-in fade-in-50 duration-300"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Chỉ số tổng quan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-black-500">
                    Lead time trung bình
                  </span>
                  <span className="font-medium">
                    {statsByTimeband?.averageLeadTimeDays != null
                      ? `${statsByTimeband.averageLeadTimeDays.toFixed(1)} ngày`
                      : "--"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-black-500">
                    Thời gian xác nhận
                  </span>
                  <span className="font-medium">
                    {statsByTimeband?.averageProcessingMinutes != null
                      ? `${statsByTimeband.averageProcessingMinutes.toFixed(
                          1
                        )} phút`
                      : "--"}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Trạng thái đăng ký</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-black-500">Chờ xử lý</span>
                  <span className="font-medium">{pendingCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-black-500">Đã xác nhận</span>
                  <span className="font-medium">{confirmedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-black-500">Đã hủy</span>
                  <span className="font-medium">{cancelledCount}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset link đăng ký?</DialogTitle>
            <DialogDescription>
              Token hiện tại sẽ bị vô hiệu hóa. QR code đã in sẽ không còn dùng
              được.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Hủy
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await onResetLink(Number(organization.id));
                setResetDialogOpen(false);
                onRefresh();
              }}
            >
              Xác nhận reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  ) : (
    <Alert variant="info" msg="Bạn không có quyền xem báo cáo thống kê của tổ chức" />
  );
}
